import React from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { 
  getMobilePadding, 
  getMobileTextSize, 
  getMobileButtonWidth,
  getTouchTargetHeight,
  cx 
} from '../../utils/mobileStyles';

function ConfirmationDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm", 
  cancelText = "Cancel", 
  confirmButtonClass = "bg-blue-500 hover:bg-blue-600", 
  extraContent = null,
  isLoading = false,
  loadingText = "Processing..."
}) {
  // useDarkMode hook is available but dark mode styling is handled via Tailwind classes
  useDarkMode();
  const isMobile = useIsMobile();
  if (!isOpen) return null;

  const handleConfirm = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Let the parent handle the async operation and closing
    // Parent should set isLoading=true before async op and call onClose when done
    await onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50 p-4" onClick={() => { if (!isLoading) onClose(); }} onKeyDown={(e) => { if (e.key === 'Escape' && !isLoading) onClose(); }} role="dialog" aria-modal="true">
      <div className={cx(
        'bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full mx-auto border border-gray-200 dark:border-gray-700',
        isMobile ? 'max-w-full' : 'max-w-md',
        getMobilePadding(isMobile)
      )} onClick={(e) => e.stopPropagation()}>
        <h3 className={cx(
          'font-semibold text-gray-900 dark:text-white mb-2',
          getMobileTextSize(isMobile, 'subheading')
        )}>{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
        
        {extraContent && (
          <div className="mb-4">
            {extraContent}
          </div>
        )}
        
        <div className={cx(
          'flex',
          isMobile ? 'flex-col-reverse gap-2' : 'justify-end space-x-3'
        )}>
          <button
            type="button"
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isLoading) onClose();
            }}
            className={cx(
              'px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-500',
              isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 dark:hover:bg-gray-600',
              getMobileButtonWidth(isMobile),
              isMobile && getTouchTargetHeight()
            )}
          >
            {cancelText}
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={handleConfirm}
            className={cx(
              'px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2',
              confirmButtonClass,
              isLoading ? 'opacity-50 cursor-not-allowed' : '',
              getMobileButtonWidth(isMobile),
              isMobile && getTouchTargetHeight()
            )}
          >
            {isLoading ? loadingText : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmationDialog;
